import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Grid,
  Stack,
  TextField,
  Typography,
  MenuItem,
} from "@mui/material";
import { CustomModal } from "@/components/Modal";
import {
  type PlotRequestTag,
  type AdditionalFieldsFormData,
} from "@/features/map/types";
import { toast } from "react-toastify";
import { useForm, type Path } from "react-hook-form";
import { useCreatePlotRequestDetail } from "@/features/map/hooks/useCreatePlotRequestDetail";
import { CAutocompleteMultiFreeSoloTextField } from "@/components/TextField/CAutocompleteMultiFreeSoloTextField";
import { SizingWrapperStyle } from "@/components/Wrapper";
import { isAxiosError } from "axios";
import { useAdditionalFieldsConfig } from "@/features/map/hooks/useAdditionalFieldsConfig";
import { convertFormDataToAdditionalFields } from "@/features/map/utils/additionalFieldsHelpers";
import { CAdditionalFieldInput } from "@/features/map/components/CAdditionalFieldInput";
import {
  preventNonNumericValueWhenKeyDown,
  preventNonNumericValueWhenPaste,
} from "@/utils/utils";
import { validateCoordinates } from "@/features/mapPlotRequest/utils/validateCoordinates";

interface IProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  plotRequestId: number;
  availableTags: PlotRequestTag[];
  onSuccess: () => void;
}

interface FormData {
  realEstateType: "土地" | "建物" | "区分建物";
  requestAddress: string;
  requestLocation: string;
  requestChiban: string;
  requestKaokuNumber: string;
  requestLatitude: string;
  requestLongitude: string;
  remarks: string;
  tags: string[];
  additionalFields?: AdditionalFieldsFormData;
}

export const AddPlotDetailModal: React.FC<IProps> = ({
  open,
  setOpen,
  plotRequestId,
  availableTags,
  onSuccess,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { trigger: createDetail } = useCreatePlotRequestDetail();
  const { availableFields, hasAvailableFields } = useAdditionalFieldsConfig();

  // 追加項目のデフォルト値を生成
  const additionalFieldsDefaultValues = React.useMemo(() => {
    if (!availableFields?.fields) return {};
    const defaults: AdditionalFieldsFormData = {};
    availableFields.fields.forEach((field) => {
      defaults[field.id] = "";
    });
    return defaults;
  }, [availableFields]);

  const {
    control,
    handleSubmit: handleFormSubmit,
    reset,
    register,
    watch,
    setValue,
    setError,
    formState: { isValid },
  } = useForm<FormData>({
    mode: "onChange",
    defaultValues: {
      realEstateType: "土地",
      requestAddress: "",
      requestLocation: "",
      requestChiban: "",
      requestKaokuNumber: "",
      requestLatitude: "",
      requestLongitude: "",
      remarks: "",
      tags: [],
      additionalFields: additionalFieldsDefaultValues,
    },
  });

  const [
    realEstateType,
    requestAddress,
    requestLocation,
    requestChiban,
    requestKaokuNumber,
    requestLatitude,
    requestLongitude,
  ] = watch([
    "realEstateType",
    "requestAddress",
    "requestLocation",
    "requestChiban",
    "requestKaokuNumber",
    "requestLatitude",
    "requestLongitude",
  ]);

  // 種別変更時に非表示になるフィールドをクリア
  useEffect(() => {
    if (realEstateType === "土地") {
      setValue("requestKaokuNumber", "");
    } else if (realEstateType === "建物" || realEstateType === "区分建物") {
      setValue("requestChiban", "");
    }
  }, [realEstateType, setValue]);

  // バリデーション: 住居表示、緯度・経度、所在・地番/家屋番号のいずれかが入力されているか
  const hasAddress = Boolean(requestAddress?.trim());
  const hasLatLng = Boolean(requestLatitude && requestLongitude);
  const hasLocationOrChiban =
    realEstateType === "土地"
      ? Boolean(requestLocation?.trim() || requestChiban?.trim())
      : Boolean(requestLocation?.trim() || requestKaokuNumber?.trim());
  const hasRequiredInput = hasAddress || hasLatLng || hasLocationOrChiban;
  const validationError = !hasRequiredInput
    ? realEstateType === "土地"
      ? "住居表示、緯度・経度、所在・地番のいずれかを入力してください"
      : "住居表示、緯度・経度、所在・家屋番号のいずれかを入力してください"
    : "";

  const handleClose = (
    _: Record<string, unknown>,
    reason: "backdropClick" | "escapeKeyDown"
  ): void => {
    if (reason === "backdropClick") {
      return;
    }
    if (!isSubmitting) {
      setOpen(false);
      reset();
    }
  };

  const handleCloseClick = (): void => {
    if (!isSubmitting) {
      setOpen(false);
      reset();
    }
  };

  const onSubmit = async (data: FormData): Promise<void> => {
    setIsSubmitting(true);
    let errorOccurred = false;

    // 緯度・経度のパースとバリデーション
    const { latitude, longitude, isValid } = validateCoordinates(
      data.requestLatitude,
      data.requestLongitude,
      setError
    );

    if (!isValid) {
      setIsSubmitting(false);
      return;
    }

    // 追加項目を新しい型に変換
    const additionalFieldsData =
      data.additionalFields && availableFields
        ? convertFormDataToAdditionalFields(
            data.additionalFields,
            availableFields
          )
        : null;

    await createDetail(
      {
        mapPlotRequestId: plotRequestId,
        realEstateType: data.realEstateType ?? "土地",
        requestAddress: data.requestAddress,
        requestLatitude: latitude,
        requestLongitude: longitude,
        requestLocation: data.requestLocation,
        requestChiban: data.requestChiban,
        requestKaokuNumber: data.requestKaokuNumber,
        remarks: data.remarks,
        substituteLatitude: null,
        substituteLongitude: null,
        additionalFields: additionalFieldsData,
        tags: data.tags,
      },
      {
        throwOnError: false,
        // NOTE: App.tsxのSWRConfigに設定されているグローバルonErrorハンドラーを上書き
        // グローバルハンドラーでは「予期しないエラーが発生しました」という汎用メッセージが表示されるが、
        // ここではバックエンドから返されるdetailフィールドの具体的なエラーメッセージを表示したいため、
        // onErrorコールバックを提供してグローバルハンドラーの実行を抑制している
        onError: (error) => {
          errorOccurred = true;
          let errorMessage = "行の追加に失敗しました";

          if (
            isAxiosError(error) &&
            error.response &&
            Array.isArray(error.response.data)
          ) {
            const messages = error.response.data
              .map((item: unknown) => {
                if (typeof item === "string") {
                  return item;
                }
                if (
                  typeof item === "object" &&
                  item !== null &&
                  "string" in item
                ) {
                  return String(item.string);
                }
                return null;
              })
              .filter((msg): msg is string => msg !== null);

            if (messages.length > 0) {
              errorMessage = messages.join("\n");
            }
          }

          toast.error(errorMessage);
        },
      }
    );

    // エラーが発生していない場合のみ成功処理を実行
    if (!errorOccurred) {
      toast.success("行を追加しました");
      onSuccess();
      handleCloseClick();
    }

    setIsSubmitting(false);
  };

  return (
    <CustomModal
      isOpen={open}
      handleClose={handleClose}
      ariaLabelledby="add-plot-detail-modal"
      ariaDescribedby="add-plot-detail-modal-description"
      width="600px"
    >
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
        {/* 固定ヘッダー */}
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
          <Typography variant="h6">行を追加</Typography>
          <Button
            variant="outlined"
            onClick={handleCloseClick}
            disabled={isSubmitting}
          >
            × 閉じる
          </Button>
        </Box>

        {/* スクロール可能なコンテンツエリア */}
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            maxHeight: "calc(90vh - 200px)",
            pr: 1,
            pt: 1,
          }}
        >
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                label="種別"
                {...register("realEstateType")}
                disabled={isSubmitting}
                value={realEstateType}
              >
                <MenuItem value="土地">土地</MenuItem>
                <MenuItem value="建物">建物</MenuItem>
                <MenuItem value="区分建物">区分建物</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="住居表示"
                placeholder="住居表示を入力"
                {...register("requestAddress")}
                disabled={isSubmitting}
                inputProps={{ maxLength: 256 }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="所在"
                placeholder="所在を入力"
                {...register("requestLocation")}
                disabled={isSubmitting}
                inputProps={{ maxLength: 256 }}
              />
            </Grid>

            {realEstateType === "土地" ? (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="地番"
                  placeholder="地番を入力"
                  {...register("requestChiban")}
                  disabled={isSubmitting}
                  inputProps={{ maxLength: 256 }}
                />
              </Grid>
            ) : (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="家屋番号"
                  placeholder="家屋番号を入力"
                  {...register("requestKaokuNumber")}
                  disabled={isSubmitting}
                  inputProps={{ maxLength: 256 }}
                />
              </Grid>
            )}

            <Grid item xs={6}>
              <TextField
                fullWidth
                label="緯度"
                placeholder="緯度を入力"
                type="number"
                {...register("requestLatitude")}
                disabled={isSubmitting}
                onKeyDown={preventNonNumericValueWhenKeyDown}
                onPaste={preventNonNumericValueWhenPaste}
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                fullWidth
                label="経度"
                placeholder="経度を入力"
                type="number"
                {...register("requestLongitude")}
                disabled={isSubmitting}
                onKeyDown={preventNonNumericValueWhenKeyDown}
                onPaste={preventNonNumericValueWhenPaste}
              />
            </Grid>

            <Grid item xs={12}>
              <CAutocompleteMultiFreeSoloTextField
                control={control}
                name="tags"
                label="タグ(複数選択可)"
                size={SizingWrapperStyle.INHERIT}
                maxLength={20}
                options={availableTags.map((tag) => tag.name)}
                disabled={isSubmitting}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="備考"
                placeholder="備考を入力"
                multiline
                rows={3}
                {...register("remarks")}
                disabled={isSubmitting}
                inputProps={{ maxLength: 200 }}
                helperText={`${watch("remarks")?.length ?? 0}/200`}
              />
            </Grid>

            {hasAvailableFields && availableFields?.fields && (
              <>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ mt: 1, mb: 1 }}>
                    カスタム項目
                  </Typography>
                </Grid>
                {availableFields.fields.map((field) => (
                  <Grid item xs={12} key={field.id}>
                    <CAdditionalFieldInput
                      control={control}
                      name={`additionalFields.${field.id}` as Path<FormData>}
                      label={field.key}
                      fieldType={field.fieldType}
                      disabled={isSubmitting}
                    />
                  </Grid>
                ))}
              </>
            )}
          </Grid>

          {validationError && (
            <Box sx={{ mt: 2 }}>
              <Typography color="error" variant="body2">
                {validationError}
              </Typography>
            </Box>
          )}
        </Box>

        {/* 固定フッター */}
        <Stack
          direction="row"
          justifyContent="center"
          spacing={2}
          sx={{ mt: 3 }}
        >
          <Button
            variant="contained"
            color="primary"
            onClick={(e) => {
              void handleFormSubmit(onSubmit)(e);
            }}
            disabled={isSubmitting || !hasRequiredInput || !isValid}
          >
            登録する
          </Button>
        </Stack>
      </Box>
    </CustomModal>
  );
};
